import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { formatApiErrorDetail } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Users, Check, ArrowRight, Landmark, Copy, Clock } from 'lucide-react';
import { Separator } from '../components/ui/separator';

const MembershipPage = () => {
  const { registerMember, googleRegister, user, isAuthenticated } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    phone: '',
    address: '',
    birth_date: '',
  });
  const [registeredUser, setRegisteredUser] = useState(null);

  const handleRegistration = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwörter stimmen nicht überein');
      return;
    }
    if (formData.password.length < 6) {
      setError('Passwort muss mindestens 6 Zeichen haben');
      return;
    }

    setLoading(true);
    try {
      const result = await registerMember({
        email: formData.email,
        password: formData.password,
        name: formData.name,
        phone: formData.phone,
        address: formData.address,
        birth_date: formData.birth_date,
      });
      setRegisteredUser(result);
      setStep(3);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Registrierung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Kopiert!');
  };

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="membership-page">
      {/* Header */}
      <section className="section-padding bg-surface">
        <div className="container-max">
          <h1 className="text-4xl md:text-5xl font-heading font-bold uppercase tracking-tighter mb-4">
            Mitglied <span className="text-primary">werden</span>
          </h1>
          <p className="text-text-secondary max-w-2xl">
            Werde Teil der House of Darts Familie. Mitgliedsbeitrag: 60€ pro Jahr.
          </p>
        </div>
      </section>

      {/* Steps */}
      <section className="py-8 border-b border-white/10">
        <div className="container-max">
          <div className="flex items-center justify-center gap-4">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                  step >= s ? 'bg-primary text-white' : 'bg-surface-elevated text-text-secondary'
                }`}>
                  {step > s ? <Check className="w-5 h-5" /> : s}
                </div>
                {s < 3 && <div className={`w-16 h-1 ${step > s ? 'bg-primary' : 'bg-surface-elevated'}`} />}
              </div>
            ))}
          </div>
          <div className="flex justify-center gap-8 mt-4 text-sm">
            <span className={step >= 1 ? 'text-white' : 'text-text-secondary'}>Info</span>
            <span className={step >= 2 ? 'text-white' : 'text-text-secondary'}>Registrierung</span>
            <span className={step >= 3 ? 'text-white' : 'text-text-secondary'}>Überweisung</span>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding">
        <div className="container-max max-w-3xl">

          {/* Step 1: Info */}
          {step === 1 && (
            <div className="text-center">
              <Card className="card-elevated max-w-lg mx-auto mb-8">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Users className="w-8 h-8 text-primary" />
                  </div>
                  <h2 className="text-2xl font-heading font-bold uppercase mb-4">Jährliche Mitgliedschaft</h2>
                  <div className="text-5xl font-heading font-bold text-primary mb-2">60€</div>
                  <p className="text-text-secondary mb-6">pro Jahr</p>

                  <ul className="text-left space-y-3 mb-8">
                    {['Zugang zu allen Trainings', 'Teilnahme an Vereinsturnieren', 'Nutzung der Vereinsräume', 'Mitglied der House of Darts Community'].map((f) => (
                      <li key={f} className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-accent flex-shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="p-4 card-surface rounded-sm text-left text-sm mb-6">
                    <div className="flex items-center gap-2 mb-2">
                      <Landmark className="w-4 h-4 text-accent" />
                      <span className="font-bold">Zahlung per Banküberweisung</span>
                    </div>
                    <p className="text-text-secondary text-xs">
                      Nach der Registrierung erhältst du die Kontodaten. Deine Mitgliedschaft wird nach Zahlungseingang vom Vorstand aktiviert.
                    </p>
                  </div>

                  <Button
                    onClick={() => isAuthenticated && user?.member_number ? setStep(3) : setStep(2)}
                    className="btn-primary text-lg px-12 py-6 w-full"
                    data-testid="step1-continue-btn"
                  >
                    {isAuthenticated && user?.member_number ? 'Zahlungsinformationen anzeigen' : 'Jetzt registrieren'}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Step 2: Registration */}
          {step === 2 && (
            <div>
              <h2 className="text-2xl font-heading font-bold uppercase tracking-tighter text-center mb-8">
                Registrierung
              </h2>
              <Card className="card-elevated max-w-xl mx-auto">
                <CardContent className="p-8">
                  <form onSubmit={handleRegistration} className="space-y-5" data-testid="registration-form">
                    {error && (
                      <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-sm text-red-400 text-sm">{error}</div>
                    )}
                    <div className="space-y-2">
                      <Label htmlFor="name">Vollständiger Name *</Label>
                      <Input id="name" className="input-dark" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="reg-name-input" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">E-Mail *</Label>
                      <Input id="email" type="email" className="input-dark" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required data-testid="reg-email-input" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="password">Passwort *</Label>
                        <Input id="password" type="password" className="input-dark" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required data-testid="reg-password-input" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="confirmPassword">Passwort bestätigen *</Label>
                        <Input id="confirmPassword" type="password" className="input-dark" value={formData.confirmPassword} onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })} required data-testid="reg-confirm-password-input" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Telefon</Label>
                      <Input id="phone" type="tel" className="input-dark" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="address">Adresse</Label>
                      <Input id="address" className="input-dark" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="birth_date">Geburtsdatum</Label>
                      <Input id="birth_date" type="date" className="input-dark" value={formData.birth_date} onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })} />
                    </div>
                    <div className="flex gap-4">
                      <Button type="button" variant="outline" onClick={() => setStep(1)} className="flex-1">Zurück</Button>
                      <Button type="submit" disabled={loading} className="btn-primary flex-1" data-testid="reg-submit-btn">
                        {loading ? 'Wird registriert...' : 'Registrieren'}
                      </Button>
                    </div>
                  </form>

                  <div className="my-6">
                    <div className="relative">
                      <Separator className="bg-white/10" />
                      <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-surface-elevated px-4 text-xs text-text-secondary">
                        oder
                      </span>
                    </div>
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    onClick={googleRegister}
                    className="w-full border-white/20 hover:bg-white/10"
                    data-testid="google-register-btn"
                  >
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                      <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Mit Google registrieren
                  </Button>

                  <div className="text-center mt-6 pt-6 border-t border-white/10">
                    <p className="text-text-secondary text-sm">
                      Bereits Mitglied?{' '}
                      <Link to="/login" className="text-primary hover:underline">Jetzt anmelden</Link>
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Step 3: Bank Transfer Info */}
          {step === 3 && (
            <div className="text-center">
              <h2 className="text-2xl font-heading font-bold uppercase tracking-tighter mb-8">
                Überweisung durchführen
              </h2>

              <Card className="card-elevated max-w-lg mx-auto">
                <CardContent className="p-8">
                  {/* Success badge */}
                  {registeredUser && (
                    <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-sm">
                      <Check className="w-8 h-8 text-green-400 mx-auto mb-2" />
                      <p className="text-green-400 font-bold">Registrierung erfolgreich!</p>
                      <p className="text-accent font-mono font-bold text-xl mt-2">{registeredUser.member_number}</p>
                      <p className="text-text-secondary text-xs mt-1">Deine Mitgliedsnummer</p>
                    </div>
                  )}

                  <div className="w-14 h-14 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Landmark className="w-7 h-7 text-accent" />
                  </div>

                  <p className="text-text-secondary mb-6">
                    Bitte überweise den Mitgliedsbeitrag auf folgendes Konto:
                  </p>

                  {/* Bank Details */}
                  <div className="card-surface p-6 rounded-sm text-left space-y-3 mb-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-text-secondary">Kontoinhaber</p>
                        <p className="font-bold">House of Darts</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-text-secondary">IBAN</p>
                        <p className="font-mono text-accent font-bold">AT22 2040 4000 4395 8800</p>
                      </div>
                      <button onClick={() => copyToClipboard('AT22204040004395 8800')} className="p-2 hover:bg-white/10 rounded-sm">
                        <Copy className="w-4 h-4 text-text-secondary" />
                      </button>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-text-secondary">BIC</p>
                        <p className="font-mono">SBGSAT2SXXX</p>
                      </div>
                      <button onClick={() => copyToClipboard('SBGSAT2SXXX')} className="p-2 hover:bg-white/10 rounded-sm">
                        <Copy className="w-4 h-4 text-text-secondary" />
                      </button>
                    </div>
                    <div className="border-t border-white/10 pt-3">
                      <p className="text-xs text-text-secondary">Betrag</p>
                      <p className="text-2xl font-heading font-bold text-primary">60,00 €</p>
                    </div>
                  </div>

                  {/* Verwendungszweck */}
                  <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-sm text-left mb-6">
                    <p className="text-yellow-400 font-bold text-sm mb-2">Verwendungszweck (wichtig!):</p>
                    <p className="text-white font-mono">
                      {registeredUser ? `${registeredUser.name} ${registeredUser.member_number}` : 'Vorname Nachname + Mitgliedsnummer'}
                    </p>
                    {registeredUser && (
                      <button
                        onClick={() => copyToClipboard(`${registeredUser.name} ${registeredUser.member_number}`)}
                        className="mt-2 text-xs text-accent hover:underline flex items-center gap-1"
                      >
                        <Copy className="w-3 h-3" /> Verwendungszweck kopieren
                      </button>
                    )}
                    <p className="text-text-secondary text-xs mt-2">
                      Beispiel: Max Mustermann HOD-000123
                    </p>
                  </div>

                  {/* Status Info */}
                  <div className="flex items-start gap-3 p-4 card-surface rounded-sm text-left">
                    <Clock className="w-5 h-5 text-text-secondary flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-bold mb-1">Was passiert jetzt?</p>
                      <p className="text-text-secondary text-xs">
                        Nach Eingang deiner Überweisung wird deine Mitgliedschaft vom Vereinsvorstand aktiviert. 
                        Du erhältst dann Zugang zu allen Vereinsfunktionen.
                      </p>
                    </div>
                  </div>

                  <div className="mt-8">
                    <Link to="/">
                      <Button className="btn-primary w-full">Zur Startseite</Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default MembershipPage;
